import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Bot, Save, Power, Settings } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AISettings = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    model_provider: 'openai',
    model_name: 'gpt-5',
    system_prompt: '',
    company_knowledge: '',
    welding_processes: '',
    material_guidelines: '',
    safety_requirements: '',
    response_tone: 'professional',
    max_tokens: 1000,
    temperature: 0.7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/ai`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/ai`, settings);
      toast.success('AI settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update AI settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <Bot className="mx-auto mb-4 animate-pulse" size={48} />
          <p>Loading AI settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
          AI Assistant Settings
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Configure AI-Powered Customer Estimate Assistant
        </p>
      </div>

      {/* Enable/Disable Toggle */}
      <Card className="border-border mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Power className={settings.enabled ? 'text-green-500' : 'text-gray-400'} size={24} />
              <div>
                <h3 className="font-bold text-lg">AI Assistant Status</h3>
                <p className="text-sm text-muted-foreground">
                  {settings.enabled ? 'AI assistant is active and helping customers' : 'AI assistant is currently disabled'}
                </p>
              </div>
            </div>
            <Button
              variant={settings.enabled ? 'destructive' : 'default'}
              onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
              className="font-mono uppercase tracking-wider"
            >
              {settings.enabled ? 'Disable' : 'Enable'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        {/* System Prompt */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-xl font-bold uppercase tracking-tight">
              System Prompt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
              Base Instructions for AI
            </Label>
            <Textarea
              value={settings.system_prompt}
              onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
              placeholder="You are a helpful welding and fabrication expert assistant..."
              className="min-h-[100px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Define the AI's role and behavior. This is the foundation of all responses.
            </p>
          </CardContent>
        </Card>

        {/* Company Knowledge */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-xl font-bold uppercase tracking-tight">
              Company Knowledge
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
              Services, Capabilities & Standards
            </Label>
            <Textarea
              value={settings.company_knowledge}
              onChange={(e) => setSettings({ ...settings, company_knowledge: e.target.value })}
              placeholder="PSM Welding specializes in...\n- Custom fabrication\n- Typical lead times: 4-6 weeks\n- Quality standards: AWS D1.1\n- Service area: Greater Toronto Area"
              className="min-h-[150px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              What services do you offer? What are your capabilities? Typical timelines?
            </p>
          </CardContent>
        </Card>

        {/* Welding Processes */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-xl font-bold uppercase tracking-tight">
              Welding Processes Knowledge
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
              Process Information & Applications
            </Label>
            <Textarea
              value={settings.welding_processes}
              onChange={(e) => setSettings({ ...settings, welding_processes: e.target.value })}
              placeholder="MIG Welding: Best for steel fabrication, fast, cost-effective\nTIG Welding: Precision work, aluminum, stainless steel\nStick Welding: Heavy construction, outdoor repairs\nFlux-Core: Windy conditions, thick materials"
              className="min-h-[150px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Explain different welding processes you use and when to apply them.
            </p>
          </CardContent>
        </Card>

        {/* Material Guidelines */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-xl font-bold uppercase tracking-tight">
              Material Guidelines
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
              Material Types, Grades & Pricing
            </Label>
            <Textarea
              value={settings.material_guidelines}
              onChange={(e) => setSettings({ ...settings, material_guidelines: e.target.value })}
              placeholder="Mild Steel: Most common, economical\nStainless Steel 304: Corrosion resistant, food-grade\nStainless Steel 316: Marine grade, chemical resistance\nAluminum: Lightweight, corrosion resistant\n\nTypical pricing ranges (per lb):\n- Mild steel: $1-2\n- Stainless 304: $3-5\n- Aluminum: $2-4"
              className="min-h-[150px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Material options, grades, applications, and general pricing guidance.
            </p>
          </CardContent>
        </Card>

        {/* Safety Requirements */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-xl font-bold uppercase tracking-tight">
              Safety & Code Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
              Building Codes, Standards & Certifications
            </Label>
            <Textarea
              value={settings.safety_requirements}
              onChange={(e) => setSettings({ ...settings, safety_requirements: e.target.value })}
              placeholder="Building Codes:\n- IBC 2021 for structural work\n- OSHA safety standards\n- AWS D1.1 structural welding code\n\nCertifications:\n- CWB certified welders\n- Engineer stamped drawings available\n- Load calculation documentation provided"
              className="min-h-[150px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Safety standards, building codes, and certifications you follow.
            </p>
          </CardContent>
        </Card>

        {/* Response Tone */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-xl font-bold uppercase tracking-tight">
              Response Style
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
                  Tone of Voice
                </Label>
                <select
                  value={settings.response_tone}
                  onChange={(e) => setSettings({ ...settings, response_tone: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm"
                >
                  <option value="professional">Professional - Formal and informative</option>
                  <option value="friendly">Friendly - Warm and approachable</option>
                  <option value="technical">Technical - Detailed and precise</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
                    Max Response Length (tokens)
                  </Label>
                  <Input
                    type="number"
                    value={settings.max_tokens}
                    onChange={(e) => setSettings({ ...settings, max_tokens: parseInt(e.target.value) })}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
                    Creativity (0.0 - 1.0)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={settings.temperature}
                    onChange={(e) => setSettings({ ...settings, temperature: parseFloat(e.target.value) })}
                    className="font-mono"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider px-8"
            size="lg"
          >
            <Save className="mr-2" size={18} />
            {saving ? 'Saving...' : 'Save AI Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AISettings;